/**
 * Tooltip — Apple glass-style hover hint.
 *
 * Portal-based, repositions on scroll/resize,
 * fade-in/out animation, macOS structured text.
 *
 * Usage:
 *   <Tooltip text="Copy to clipboard">
 *     <IconButton icon={<Copy className="w-4 h-4" />} label="Copy" />
 *   </Tooltip>
 *
 *   <Tooltip text="Title\nBody line" variant="glass" size="md">
 *     ...
 *   </Tooltip>
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ds } from '../tokens';

export interface TooltipProps {
  text: string;
  children: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  variant?: 'default' | 'glass';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
}

const SIZE_CLASSES = {
  sm: 'px-3 py-1.5 min-h-[28px] max-w-[240px]',
  md: 'px-3.5 py-2 min-h-[30px] max-w-[280px]',
  lg: 'px-3.5 py-2 min-h-[32px] max-w-[320px]',
} as const;

const VARIANT_CLASSES = {
  default: `${ds.color.fill.dark} ${ds.color.label.inverse} ${ds.shadow.tooltip}`,
  glass: `${ds.color.fill.primary} ${ds.color.label.primary} ${ds.shadow.tooltip}`,
} as const;

/** macOS structured text: first line = title (13px medium), rest = body (12px regular) */
function renderStructuredText(raw: string) {
  const lines = raw.split('\n');
  const title = lines[0];
  const body = lines.slice(1).filter(l => l.length > 0);

  if (body.length === 0) {
    return <span className={`${ds.type.footnoteMedium} leading-[1.4]`}>{title}</span>;
  }

  return (
    <>
      <div className={`${ds.type.footnoteMedium} leading-[1.4]`}>{title}</div>
      <div className={`${ds.type.subFootnote} ${ds.type.weight.regular} ${ds.color.label.caption} leading-[1.4] mt-0.5`}>
        {body.map((line, i) => <div key={i}>{line}</div>)}
      </div>
    </>
  );
}

export function Tooltip({
  text,
  children,
  position = 'top',
  delay = 300,
  variant = 'glass',
  size = 'sm',
  disabled = false,
  className = '',
}: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; transform: string }>({ top: 0, left: 0, transform: 'none' });
  const triggerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  // Immediately dismiss when disabled
  useEffect(() => {
    if (disabled) {
      clearTimeout(timerRef.current);
      setVisible(false);
      setShouldRender(false);
    }
  }, [disabled]);

  const computePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 8;

    switch (position) {
      case 'top':
        setCoords({ top: rect.top - gap, left: rect.left + rect.width / 2, transform: 'translateX(-50%) translateY(-100%)' });
        break;
      case 'bottom':
        setCoords({ top: rect.bottom + gap, left: rect.left + rect.width / 2, transform: 'translateX(-50%)' });
        break;
      case 'left':
        setCoords({ top: rect.top + rect.height / 2, left: rect.left - gap, transform: 'translateX(-100%) translateY(-50%)' });
        break;
      case 'right':
        setCoords({ top: rect.top + rect.height / 2, left: rect.right + gap, transform: 'translateY(-50%)' });
        break;
    }
  }, [position]);

  const show = useCallback(() => {
    if (disabled) return;
    timerRef.current = setTimeout(() => {
      computePosition();
      setShouldRender(true);
      setTimeout(() => setVisible(true), 20);
    }, delay);
  }, [delay, disabled, computePosition]);

  const hide = useCallback(() => {
    clearTimeout(timerRef.current);
    setVisible(false);
    setTimeout(() => setShouldRender(false), 200);
  }, []);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Recompute position on scroll/resize while visible
  useEffect(() => {
    if (!shouldRender) return;
    const handler = () => computePosition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [shouldRender, computePosition]);

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        className={['inline-flex', className].join(' ')}
      >
        {children}
      </div>

      {shouldRender && createPortal(
        <div
          className={[
            'fixed',
            ds.zIndex.tooltip,
            SIZE_CLASSES[size],
            VARIANT_CLASSES[variant],
            ds.radius.popover,
            'text-left pointer-events-none select-none',
            'transition-opacity duration-150 ease-out',
            visible ? 'opacity-100' : 'opacity-0',
          ].join(' ')}
          style={{ top: coords.top, left: coords.left, transform: coords.transform }}
          role="tooltip"
        >
          {renderStructuredText(text)}
        </div>,
        document.body,
      )}
    </>
  );
}
